import React, { useEffect, useState } from 'react';

import {
  AsyncTaskType,
  CareTeamRosterTaskStatus,
} from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  PaginationType,
  PodShuffleAsyncTask,
} from '@vestahealthcare/common/models';

import {
  CSVFileInfo,
  DEFAULT_VALIDATION_RESP,
} from 'dash/src/pages/Ops/MemberIngestion';
import { CSVUpload } from 'dash/src/pages/Ops/MemberIngestion/CSVUpload';
import { FileContentTable } from 'dash/src/pages/Ops/MemberIngestion/FileContentTable';
import { FileInfoPanel } from 'dash/src/pages/Ops/MemberIngestion/FileInfoPanel';
import { PreUploadPodShuffleColumns } from 'dash/src/pages/Ops/MemberIngestion/MemberTableColumns';
import { UploadHistoryTable } from 'dash/src/pages/Ops/MemberIngestion/UploadHistoryTable';
import { ParsedFile } from 'dash/src/pages/Ops/MemberIngestion/utils';
import { ValidationResponse } from 'dash/src/services/RosterServices';

import {
  bulkPodShuffleUpload,
  bulkPodShuffleValidate,
  getPodShuffleRoster,
} from '../../../../../services/PodServices';

interface Props {
  setLoading: (loading: boolean) => void;
}

export const PODShuffleRosterTab = ({ setLoading }: Props) => {
  const [fileInfo, setFileInfo] = useState<CSVFileInfo | undefined>();
  const [pastUploads, setPastUploads] = useState<PodShuffleAsyncTask[]>([]);
  const [validating, setValidating] = useState<boolean>(false);
  const [validationResponse, setValidationResponse] = useState<
    ValidationResponse
  >(DEFAULT_VALIDATION_RESP);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [errorValidate, setErrorValidate] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    limit: 10,
    offset: 0,
  });

  const fetchData = async (limit: number, offset: number) => {
    try {
      if (pastUploads.length === 0) setLoading(true);
      const { items, pagination: newPagination } = await getPodShuffleRoster({
        limit,
        offset,
      });

      if (offset > 0) {
        setPastUploads((prev) => [...prev, ...items]);
      } else {
        setPastUploads(items);
      }

      setPagination({
        limit: newPagination.limit,
        offset: newPagination.offset,
        total: newPagination.total,
      });

      const hasInProgress = items.some(
        (upload: PodShuffleAsyncTask) =>
          upload.status === CareTeamRosterTaskStatus.IN_PROGRESS.value,
      );
      setUploadInProgress(hasInProgress);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.limit, pagination.offset);
  }, []);

  useEffect(() => {
    if (uploadInProgress) {
      const timeout = setTimeout(
        () => fetchData(pagination.limit, pagination.offset),
        500,
      );
      return () => clearTimeout(timeout);
    }
  }, [uploadInProgress, pagination]);

  const handleFileChange = (parsedFile: ParsedFile) => {
    setFileInfo({
      file: parsedFile.file,
      headers: parsedFile.headers,
      fileContents: parsedFile.fileContents,
      isCareTeamFile: false,
    });
  };

  const validate = async (file: File) => {
    setValidationResponse(DEFAULT_VALIDATION_RESP);
    setValidating(true);
    setErrorValidate(false);

    try {
      const resp = await bulkPodShuffleValidate(file);
      setValidationResponse(resp);
    } catch (error) {
      setErrorValidate(true);
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!fileInfo) return;

    try {
      setUploading(true);

      const resp = await bulkPodShuffleUpload(fileInfo.file);

      if (resp?.validationResult) {
        setValidationResponse(resp.validationResult);
      } else {
        setFileInfo(undefined);
        setValidationResponse(DEFAULT_VALIDATION_RESP);
      }

      if (!resp?.validationResult) {
        await fetchData(pagination.limit, 0);
      }
    } catch (error) {
      setErrorValidate(true);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (fileInfo?.file && !validating) {
      validate(fileInfo.file);
    }
  }, [fileInfo]);

  return (
    <div className="grid-12 gap">
      <CSVUpload onFileChange={handleFileChange} />

      {fileInfo?.file && (
        <FileInfoPanel
          fileInfo={fileInfo}
          onUpload={handleUpload}
          validating={validating}
          validationResponse={validationResponse}
          uploadInProgress={uploading}
          errorValidate={errorValidate}
          rosterType={AsyncTaskType.POD_SHUFFLE_ROSTER}
        />
      )}

      <FileContentTable
        validationResponse={validationResponse}
        fileInfo={fileInfo}
        columns={PreUploadPodShuffleColumns}
      />

      <div className="grid-span-12">
        <h2 className="size-l mb-3">
          {translate('pods.shuffle.roster.pastUploads')}
        </h2>
        <UploadHistoryTable
          pastUploads={pastUploads}
          pagination={pagination}
          onChangePage={(page) => {
            const newOffset = page * pagination.limit;
            setPagination({
              ...pagination,
              offset: newOffset,
            });
            fetchData(pagination.limit, newOffset);
          }}
          onChangePageSize={(pageSize) => {
            setPagination({
              ...pagination,
              limit: pageSize,
              offset: 0,
            });
            fetchData(pageSize, 0);
          }}
          rosterType={AsyncTaskType.POD_SHUFFLE_ROSTER}
        />
      </div>
    </div>
  );
};
